<template>
    <form class="ds-login-form" @submit.prevent="Login" @focusin="syncAutofilledValues" @change="syncAutofilledValues">
        <h3 class="ds-login-title mb-6">Вход</h3>
        <div v-if="isPhone">
            <phone-input
                ref="phoneInputRef"
                v-model="phone"
                class="mb-1"
                size="md"
                autocomplete="tel"
                @input="validation()"
            >
                Телефон
            </phone-input>
        </div>
        <Input
            v-else
            ref="emailInputRef"
            v-model="email"
            class="mb-1"
            autocomplete="username"
            max-length="30"
            @input="validation()"
        >
            E-mail
        </Input>
        <div @click="changeLogin" class="cursor-pointer mb-4 text-graylight">
            <span v-if="isPhone">Войти по e-mail</span>
            <span v-else>Войти по номеру телефона</span>
        </div>
        
        <Input
            ref="passwordInputRef"
            v-model="password"
            type="password"
            class="mb-3"
            autocomplete="current-password"
            max-length="30"
        >
            Пароль
        </Input>
        <div class="cursor-pointer mb-4 text-graylight">
            <router-link to="/recovery">Забыли пароль?</router-link>
        </div>
        <Button class="mb-2 mt-8" :disabled="isDisabled" :class="{'opacity-50': isDisabled}">Войти</Button>
    </form>
</template>

<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import { useAuthStore } from '@/stores/authStore'
import Input from '@/ui/Input.vue'
import Button from '@/ui/Button.vue'

type ValueInputRef = {
    getValue: () => string
}

type PhoneInputRef = {
    getValue: () => string
    syncFromInput: () => void
}

const email = ref("")
const password = ref("")
const phone = ref('')
const isPhone = ref(true)
const isDisabled = ref(true)
const isSubmitting = ref(false)
const emailInputRef = ref<ValueInputRef | null>(null)
const passwordInputRef = ref<ValueInputRef | null>(null)
const phoneInputRef = ref<PhoneInputRef | null>(null)
const syncTimers: number[] = []

const store = useAuthStore()

async function Login() {
    syncAutofilledValues()
    if (isSubmitting.value) return
    isSubmitting.value = true
    isDisabled.value = true
    const username = isPhone.value ? phone.value : email.value
    const data = {
        login: username,
        password: password.value
    }
    try {
        await store.signIn(data)
    } finally {
        isSubmitting.value = false
        isDisabled.value = false
    }
}

function validation() {
    const re = /^(?!.*\.\.)[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/
    isDisabled.value = !((isPhone.value && phone.value.length > 6) || (!isPhone.value && re.test(email.value)))
}

function changeLogin() {
    isPhone.value = !isPhone.value
    syncAutofilledValues()
    validation()
}

function syncAutofilledValues() {
    const nextPassword = passwordInputRef.value?.getValue() ?? password.value
    if (nextPassword !== password.value) {
        password.value = nextPassword
    }

    if (isPhone.value) {
        phoneInputRef.value?.syncFromInput()
        const nextPhone = phoneInputRef.value?.getValue() ?? phone.value
        if (nextPhone !== phone.value) {
            phone.value = nextPhone
        }
    } else {
        const nextEmail = emailInputRef.value?.getValue() ?? email.value
        if (nextEmail !== email.value) {
            email.value = nextEmail
        }
    }

    validation()
}

onMounted(() => {
    nextTick(() => {
        syncAutofilledValues()
        syncTimers.push(window.setTimeout(syncAutofilledValues, 180))
        syncTimers.push(window.setTimeout(syncAutofilledValues, 900))
    })
})

onBeforeUnmount(() => {
    syncTimers.forEach((timerId) => window.clearTimeout(timerId))
})

</script>
